import { PointCloud } from ".";
import {
  BufferAttribute,
  BufferGeometry,
  LineSegments,
  Points,
  Sphere,
  Vector3
} from "three";

export class CloudNode {
    protected cloud: PointCloud;
    public name = null;
    public parent: CloudNode;
    public pointsScene: Points = null;
    public pointsPicker: Points = null;
    protected boundingBox: LineSegments = null;
    protected loaded: boolean;
    protected loading: boolean;
    protected size;
    protected sizeTight = new Vector3();
    protected data = null;
    protected _center = null;
    protected _sphere = null;
    public visible = false;
    protected numPoints = null;
    protected geometry = null;
    protected tightBoundingBox = null;

    constructor() {
      this.loaded = false;
      this.loading = false;
    }

    /** Return bounding sphere in viewer coordinates */
    get boundingSphere() {
      if (this._sphere === null) {
        this._sphere = new Sphere(this.center, this.radius);
      }

      return this._sphere;
    }

    get shift() {
      return new Vector3();
    }

    /** Return node center in viewer coordinates */
    get center() {
      return new Vector3().add(this._center).add(this.shift);
    }

    /** Return bounding sphere radius */
    get radius() {
      return Math.sqrt(3) * this.size / 2.0;
    }

    isLoaded() {
      return this.loaded;
    }

    isGeometryNode() {
      return this.pointsScene === null;
    }

    isTreeNode() {
      return this.pointsScene !== null;
    }

    removeFromScene(item) {
      if (!item) return;
      this.cloud.scene.remove(item);
    }

    disposeGeometry(item) {
      if (!item) {
        return;
      }

      item.geometry.dispose();
      item.material.dispose();
    }

    dispose() {
      this.removeFromScene(this.pointsScene);
      this.disposeGeometry(this.pointsScene);
      this.pointsScene = null;

      this.cloud.picker.remove(this.pointsPicker);
      this.disposeGeometry(this.pointsPicker);
      this.pointsPicker = null;

      if (this.boundingBox) {
        this.removeFromScene(this.boundingBox);
        this.disposeGeometry(this.boundingBox);
        this.boundingBox = null;
      }

      if (this.geometry) {
        this.geometry.dispose();
      }
      this.geometry = null;

      this.loaded = false;
      this.loading = false;

      // console.log(`Dispose node: ${this.name}`);
    }

    setVisibility(visible, boundingBoxVisibility) {
      if (this.pointsPicker) {
        this.pointsPicker.visible = visible;
      }

      if (this.pointsScene) {
        this.pointsScene.visible = visible;
      }

      if (this.boundingBox) {
        this.boundingBox.visible = boundingBoxVisibility;
      }

      this.visible = visible;
    }

    setGeometry(data) {
      let geometry = new BufferGeometry();
      geometry.setAttribute('position', new BufferAttribute(data.positions, 3));
      geometry.setAttribute('intensity', new BufferAttribute(data.intensity, 1));
      geometry.setAttribute('classification', new BufferAttribute(data.classification, 1));
      geometry.setAttribute('rgb', new BufferAttribute(data.colors, 3));
      geometry.setAttribute('gpu_index', new BufferAttribute(data.gpu_index, 1));
      geometry.computeBoundingBox();
      this.geometry = geometry;
    }

    addCloudData(pointsPicker, pointsScene, boundingBox) {
      // Add our global shift to line up with previous data
      pointsScene.position.copy(this.shift);
      pointsPicker.position.copy(this.shift);

      // Add our new objects
      this.pointsPicker = pointsPicker;
      this.pointsScene = pointsScene;

      this.cloud.scene.add(this.pointsScene);
      this.cloud.picker.add(this.pointsPicker);

      if (boundingBox) {
        // Bounding box is for dev mode only
        boundingBox.position.copy(this.center);
        this.boundingBox = boundingBox;
        this.cloud.scene.add(this.boundingBox);
      }
    }
}